import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { ErrorHandlerService } from "./error-handler.service";
import { API_URLS } from "../config/url.config";
import { LIMIT_HISTORY } from "../config/global.config";
import { TokenResponse } from "@app/core/models/auth.model";

@Injectable({
  providedIn: "root",
})
export class HttpService {
  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  login<T>(data: any): Observable<HttpResponse<string>> {
    return this.http.post<string>(API_URLS.BASE_URL_LOGIN, data, {
      observe: 'response',
      responseType: 'text' as 'json'
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  changePassword<T>(data: any): Observable<HttpResponse<string>> {
    const params = new HttpParams()
      .set('username', data.username)
      .set('password', data.password)
      .set('newPassword', data.newPassword)
      .set('confirmPassword', data.confirmPassword);

    return this.http.post<string>(API_URLS.BASE_URL_LOGIN, null, {
      observe: 'response',
      responseType: 'text' as 'json',
      params: params
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  logout<T>(): Observable<T> {
    return this.http.get<T>(API_URLS.BASE_URL_LOGOUT).pipe(
      catchError((error : HttpErrorResponse) => {
        return throwError(() => error);
      }),
    );
  }

  getData<T>(endpoint: string, params: Object): Observable<T> {
    return this.http.post<T>(`${endpoint}`, params).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error fetching data: ${error}`));
      }),
    );
  }

  readRecord<T>(model: string, id: string | number): Observable<T> {
    return this.http.get<T>(API_URLS.BASE_URL_REST + `/${model}/${id}`).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error reading record: ${error}`));
      }),
    );
  }

  updateRecord<T>(model: string, id: string, body: any): Observable<T> {
    return this.http.post<T>(API_URLS.BASE_URL_REST + `/${model}/${id}`, body).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error updating record: ${error}`));
      }),
    );
  }

  createRecord<T>(model: string, body: any): Observable<T> {
    return this.http.put<T>(API_URLS.BASE_URL_REST + `/${model}`, body).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error creating record: ${error}`));
      }),
    );
  }

  getModelProperties<T>(model: string): Observable<T> {
    return this.http.get<T>(API_URLS.BASE_URL_FIELDS + `/${model}`).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error fetching model properties: ${error}`));
      }),
    );
  }

  getModelView<T>(body: any): Observable<T> {
    return this.http.post<T>(API_URLS.BASE_URL_VIEW, body).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error fetching model properties: ${error}`));
      }),
    );
  }

  advancedSearch<T>(model: string, params: object): Observable<T> {
    return this.http.post<T>(API_URLS.BASE_URL_REST + `/${model}/search`, params).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error advanced search: ${error}`));
      }),
    );
  }

  viewSave<T>(params: Object): Observable<T> {
    return this.http.post<T>(API_URLS.BASE_URL_VIEW + `/save`, params).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error advanced search: ${error}`));
      }),
    );
  }

  exportFields<T>(model: string, params: Object): Observable<T> {
    return this.http.post<T>(API_URLS.BASE_URL_REST + `/${model}/export`, params).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error export fields: ${error}`));
      }),
    );
  }

  getHistory<T>(id: number, model: string): Observable<T> {
    return this.http.get<T>(API_URLS.BASE_URL_HISTORY + `${LIMIT_HISTORY}&relatedId=${id}&relatedModel=${model}`).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error deleting record: ${error}`));
      }),
    );
  }

  actionRequest<T>(action: string, params: Object, model: string | null): Observable<T> {
    const body = {
      action,
      model,
      data: {
        context: params,
        criteria: [],
      },
    };

    return this.http.post<T>(API_URLS.BASE_URL_ACTION, body).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error action request: ${error}`));
      }),
    );
  }

  actionRequestList<T>(body: any): Observable<T> {
    return this.http.post<T>(API_URLS.BASE_URL_ACTION, body).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error action request: ${error}`));
      }),
    );
  }

  actionRequestParams<T>(url: string, body: any): Observable<T> {
    return this.http.post<T>(url, body).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error action request: ${error}`));
      }),
    );
  }

  getFetchRecord<T>(model: string, id: string | number, params: any): Observable<T> {
    return this.http.post<T>(API_URLS.BASE_URL_REST + `/${model}/${id}/fetch`, params).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error reading record: ${error}`));
      }),
    );
  }

  getMenu<T>(): Observable<T> {
    return this.http.get<T>(API_URLS.BASE_URL_MENU).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error fetching menu: ${error}`));
      }),
    );
  }

  getAppInfo<T>(): Observable<HttpResponse<T>> {
    return this.http.get<T>(API_URLS.BASE_URL_APP_INFO, { observe: 'response' }).pipe(
      catchError((error) => {
        this.errorHandlerService.handle(error, false, true);
        return throwError(() => new Error(`Error fetching app info: ${error}`));
      }),
    );
  }

}
